import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import {
  CreateUpdateAttachmentDto,
  CreateUpdateManualDto,
  GetAttachmentDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import DragNDropFiles, {
  NewFile,
} from '../../../common/components/File/DragNDropFiles';
import TestMultiSelector from '../Selects/TestMultiSelector';

interface ManualEditorProps {
  id?: string;
  manual: CreateUpdateManualDto;
  handleClose?: () => void;
  show?: boolean;
  readOnly?: boolean;
  existingAttachments?: GetAttachmentDto[];
  filesToAdd: NewFile[];
  filesToDelete: string[];
  setFilesToAdd: React.Dispatch<React.SetStateAction<NewFile[]>>;
  setFilesToDelete: React.Dispatch<React.SetStateAction<string[]>>;
  setMaxFileSizeExceeded: React.Dispatch<React.SetStateAction<boolean>>;
  onCompleted: (
    manual: CreateUpdateManualDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ManualEditor: React.FC<ManualEditorProps> = props => {
  const [manual, setManual] = useState(props.manual);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const spd = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setManual(props.manual);
    }
  }, [props.show, props.manual, props.id]);

  const saveManual = async () => {
    const attachments: CreateUpdateAttachmentDto[] = props.filesToAdd.map(
      file => ({
        date: new Date(),
        name: file.name,
        description: 'Załącznik podręcznika',
        file: file.content.toString('base64'),
      }),
    );

    props
      .onCompleted(
        {
          ...manual,
          attachmentsToDelete: props.filesToDelete,
          attachments,
        },
        props.id,
      )
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        console.error('Error during save manual:', error);
      });
  };

  const propertyChange = (obj: Partial<CreateUpdateManualDto>) => {
    setManual({ ...manual, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id ? 'Edycja podręcznika' : 'Dodawanie nowego podręcznika'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="mb-2">
          <Card.Header>Podręcznik</Card.Header>
          <Card.Body>
            <Form className="d-grid gap-3">
              <FormRow controlId="manual_name" label="Nazwa">
                <Form.Control
                  type="text"
                  value={manual?.name || ''}
                  onChange={e => propertyChange({ name: e.target.value })}
                  disabled={props.readOnly}
                />
              </FormRow>
              <FormRow controlId="manual_description" label="Opis">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={manual?.description || ''}
                  onChange={e =>
                    propertyChange({ description: e.target.value || null })
                  }
                  disabled={props.readOnly}
                />
              </FormRow>
              <FormRow controlId="manual_tests" label="Wybrane testy">
                <TestMultiSelector
                  selectedTestIds={manual.testsIds || []}
                  onChange={options =>
                    propertyChange({
                      testsIds: (options || []).map(option => option.value),
                    })
                  }
                  readOnly={props.readOnly}
                />
              </FormRow>
              <FormRow controlId="manual_attachments" label="Załącznik">
                <DragNDropFiles
                  existingFiles={props.existingAttachments?.map(file => ({
                    id: file.id,
                    name: file.name || 'file',
                    size: Number(file.fileSize) || 0,
                  }))}
                  setFilesToAdd={props.setFilesToAdd}
                  setFilesToDelete={props.setFilesToDelete}
                  setMaxFileSizeExceeded={props.setMaxFileSizeExceeded}
                  readOnly={props.readOnly}
                />
              </FormRow>
            </Form>
          </Card.Body>
        </Card>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={spd.manuals.getLastEditor} />
        )}
        <Button variant="outline-secondary" onClick={props.handleClose}>
          Zamknij
        </Button>
        {!props.readOnly && (
          <Button variant="outline-primary" onClick={saveManual}>
            Zapisz
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
